import styled from 'styled-components';
import { v } from '../../styles/variables';
import { PrimaryButton, UnstyledButton, IconButton } from '../common/Button';

// Layout Components
export const FeedAppContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const FeedContainer = styled.div`
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  max-width: 550px; /* Narrowed for 9:16 video optimization */
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 10px 0;
    padding-bottom: 20px;
  }
`;

// Post Container Components
export const PostContainer = styled.div`
  width: calc(100% - 24px);
  box-sizing: border-box;
  max-width: 500px; /* Narrowed for 9:16 video optimization */
  margin: 0 auto 20px;
  border-radius: 50px;
  background: ${({theme}) => theme.colors.bg2};
  padding: ${v.smSpacing};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.08);
  border: none;
  content-visibility: auto;
  contain-intrinsic-size: auto 500px;

  @media (max-width: 768px) {
    border-radius: 25px;
    margin: 0 auto 12px;
  }
`;

// Post Header Components
export const PostHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${({theme}) => theme.colors.divider};
  position: relative;
  overflow: visible;
  
  .post-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    overflow: visible;
  }
`;

export const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

export const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
`;

export const UserTextInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Name = styled.span`
  font-weight: 600;
  font-size: ${v.fontSizeMain};
`;

export const Username = styled.span`
  color: ${({theme}) => theme.colors.text};
  font-size: ${v.fontSizeMain};
  font-style: italic;
`;

export const VerifiedBadge = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  background: url('/src/assets/verified.svg') no-repeat center center;
  background-size: contain;
  vertical-align: middle;
`;

export const PostTypeBadge = styled.div`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  
  ${({ type, theme }) => {
    switch (type) {
      case 'PAID':
        return `
          background-color: ${theme.colors.primary};
          color: white;
        `;
      case 'TARGET':
        return `
          background-color: #4CAF50;
          color: white;
        `;
      case 'SUBSCRIBERS_ONLY':
        return `
          background-color: #9C27B0;
          color: white;
        `;
      case 'FOLLOWERS_ONLY':
        return `
          background-color: #2196F3;
          color: white;
        `;
      default:
        return '';
    }
  }}
`;

// Post Content Components
export const PostContent = styled.div`
  padding: 16px;
`;

export const PostDescription = styled.div`
  margin: 0;
  font-size: ${v.fontSizeMain};
  line-height: 1.5;
  word-break: break-word;

  p {
    margin: 0.5em 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  pre {
    background: ${({theme}) => theme.colors.bg3};
    border-radius: 4px;
    padding: 8px 12px;
    font-family: monospace;
    overflow-x: auto;
    margin: 1em 0;
  }

  ul, ol {
    padding-left: 24px;
    margin: 0.5em 0;
  }

  blockquote {
    border-left: 3px solid ${({theme}) => theme.colors.primary};
    padding-left: 12px;
    color: ${({theme}) => theme.colors.textSecondary};
    margin: 1em 0;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  * {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Create Post Components
export const ComposeBox = styled.div`
  background: ${({theme}) => theme.colors.background};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  position: relative;

  &.dragging {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${({theme}) => `${theme.colors.primary}11`};
      border: 2px dashed ${({theme}) => theme.colors.primary};
      border-radius: 16px;
      pointer-events: none;
    }
  }
`;

export const ComposeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ComposeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const ComposeInput = styled.div`
  width: 100%;
  margin-bottom: 16px;

  .tiptap {
    min-height: 100px;
    padding: 12px;
    font-family: inherit;
    border: 1px solid ${({theme}) => theme.colors.divider};
    border-radius: 8px;
    background: transparent;
    color: ${({theme}) => theme.colors.text};
    font-size: ${v.fontSizeMain};
    
    /* Mobile Safari zoom prevention */
    @media (max-width: 768px) {
      font-size: 16px; /* Minimum 16px to prevent iOS Safari zoom */
    }
    
    &:focus {
      outline: none;
      border-color: ${({theme}) => theme.colors.primary};
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: ${({theme}) => theme.colors.textSecondary};
      pointer-events: none;
      height: 0;
    }

    pre {
      background: ${({theme}) => theme.colors.bg3};
      border-radius: 4px;
      padding: 8px 12px;
      font-family: monospace;
    }

    ul, ol {
      padding-left: 24px;
      margin: 0.5em 0;
    }

    blockquote {
      border-left: 3px solid ${({theme}) => theme.colors.primary};
      padding-left: 12px;
      color: ${({theme}) => theme.colors.textSecondary};
      margin: 1em 0;
    }

    strong {
      font-weight: 600;
    }

    em {
      font-style: italic;
    }
  }
`;

// Format Toolbar Components
export const FormatToolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 8px;
  border-bottom: 1px solid ${({theme}) => theme.colors.border};
  margin-bottom: 8px;
`;

export const FormatButton = styled(IconButton).attrs({ $size: 'small' })`
  background: ${({$active, theme}) => $active ? theme.colors.bg3 : 'transparent'};
  color: ${({$active, theme}) => $active ? theme.colors.primary : theme.colors.text};

  &:active:not(:disabled) {
    background: ${({theme}) => theme.colors.primaryLight};
  }
`;

// Target Progress Components
export const TargetProgress = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: ${({theme}) => theme.colors.bg3};
  border-radius: 8px;
  
  .progress-bar {
    height: 8px;
    background: ${({theme}) => theme.colors.divider};
    border-radius: 4px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: ${({theme}) => theme.colors.primary};
      transition: width 0.3s ease;
    }
  }
  
  .target-info {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 14px;
    color: ${({theme}) => theme.colors.text};
  }
`;

// Post Actions Components
export const PostActions = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 24px;
  border-top: 1px solid ${({theme}) => theme.colors.divider};
`;

export const ActionCount = styled.span`
  margin-left: 4px;
`;

// Settings Components
export const SettingsMenu = styled.div`
  position: absolute;
  right: 40px;
  top: 40px;
  background: ${({theme}) => theme.colors.bg2};
  box-shadow: 0 1px 8px ${({theme}) => theme.colors.shadow};
  border-radius: 30px 0 30px 30px;
  overflow: hidden;
  z-index: 10;
  min-width: 180px;
  transform-origin: top right;
  animation: scaleIn 0.2s ease forwards;
  
  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.5);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// Comments Section Components
export const CommentsSection = styled.div`
  padding: 0 16px 16px;
  border-top: 1px solid ${({theme}) => theme.colors.divider};
  margin-top: 8px;
`;

export const CommentItem = styled.div`
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid ${({theme}) => theme.colors.divider};
  &:last-child {
    border-bottom: none;
  }
`;

export const CommentAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
`;

export const CommentContent = styled.div`
  flex: 1;
`;

export const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

export const CommentUserName = styled.span`
  font-weight: 600;
  font-size: ${v.fontSizeMain};
  margin-right: 8px;
`;

export const CommentTime = styled.span`
  color: ${({theme}) => theme.colors.textSecondary};
  font-size: 12px;
`;

export const CommentText = styled.p`
  margin: 0;
  font-size: ${v.fontSizeMain};
  line-height: 1.4;
`;

export const CommentForm = styled.form`
  display: flex;
  gap: 12px;
  padding: 16px 0;
`;

export const CommentInput = styled.input`
  flex: 1;
  padding: 8px 16px;
  border: 1px solid ${({theme}) => theme.colors.divider};
  border-radius: 20px;
  font-size: ${v.fontSizeMain};
  background: transparent;
  color: ${({theme}) => theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${({theme}) => theme.colors.primary};
  }
`;

export const NoCommentsMessage = styled.div`
  text-align: center;
  padding: 24px;
  color: ${({theme}) => theme.colors.textSecondary};
`;

export const CommentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
`;

export const CommentLikeCount = styled.span`
  font-size: 12px;
`;

// Create Post Components
export const CreatePostModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  border-radius: 50px;
  z-index: 1001;
  padding: 20px;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
`;

export const LockedContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
  border-radius: 16px;
  overflow: hidden;
  background: ${({theme}) => theme.colors.bg2};
  
  .locked-placeholder {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    filter: blur(10px);
    transform: scale(1.1);
  }
  
  .locked-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 24px;
    color: white;
    text-align: center;
    
    .lock-icon {
      width: 64px;
      height: 64px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
      
      svg {
        color: white;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
      }
    }
    
    p {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
    
    .price {
      font-size: 24px;
      font-weight: 700;
      color: ${({theme}) => theme.colors.primary};
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
    
    .unlock-button {
      background: ${({theme}) => theme.colors.primary};
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 50px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        background: ${({theme}) => theme.colors.primaryLight};
      }
      
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

// Media Components
export const MediaContainer = styled.div`
  position: relative;
  margin: 12px 0;
  border-radius: 8px;
  overflow: hidden;

  .media-scroll {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .media-item {
    flex: 0 0 100%;
    scroll-snap-align: start;
    position: relative;
    background: ${({theme}) => theme.colors.bg3};
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &.locked {
      .locked-content {
        position: relative;
        width: 100%;
        height: 100%;
        min-height: 300px;
      }

      .locked-placeholder {
        width: 100%;
        height: 100%;
        object-fit: cover;
        filter: blur(10px);
        transform: scale(1.1);
      }

      .locked-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${({theme}) => theme.shadows.md};
        backdrop-filter: blur(8px);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding: 24px;
        color: white;
        text-align: center;

        .lock-icon {
          width: 64px;
          height: 64px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          
          svg {
            filter: drop-shadow(0 2px 4px ${({theme}) => theme.shadows.md});
          }
        }

        p {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          text-shadow: 0 2px 4px ${({theme}) => theme.shadows.md};
        }

        .price {
          font-size: 24px;
          font-weight: 700;
          color: ${({theme}) => theme.colors.primary};
          text-shadow: 0 2px 4px ${({theme}) => theme.shadows.md};
        }

        .unlock-button {
          background: ${({theme}) => theme.colors.primary};
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 50px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px ${({theme}) => theme.shadows.md};
            background: ${({theme}) => theme.colors.shadow};
          }
        }
      }
    }

    &.preview {
      .preview-badge {
        position: absolute;
        top: 12px;
        right: 12px;
        background: ${({theme}) => theme.shadows.md};
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
      }
    }

    .image-container {
      width: 100%;
      height: 100%;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &.error {
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f8f8f8;
        min-height: 200px;

        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #666;

          svg {
            opacity: 0.5;
          }

          span {
            font-size: 14px;
          }
        }
      }
    }
  }

  .media-nav {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    justify-content: space-between;
    padding: 0 12px;
    pointer-events: none;
    z-index: 2;
    
    .nav-button {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: ${({theme}) => theme.shadows.md};
      border: none;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      transition: all 0.2s ease;
      backdrop-filter: blur(4px);
      
      &:hover {
        background: ${({theme}) => theme.shadows.md};
        transform: scale(1.1);
      }
    }
  }
  
  .media-dots {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    z-index: 2;
    
    .media-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.5);
      border: none;
      padding: 0;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &.active {
        background: white;
        transform: scale(1.2);
      }
      
      &:hover {
        background: rgba(255, 255, 255, 0.8);
      }
    }
  }
`;

// Media Preview Components
export const MediaGridContainer = styled.div`
  margin-top: 16px;
`;

export const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-top: 8px;
`;

export const MediaPreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: ${({theme}) => theme.colors.bg3};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  
  .controls {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
  }
  
  .preview-control {
    position: absolute;
    bottom: 4px;
    left: 4px;
    z-index: 2;
  }
`;

// Poll Components
export const PollOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  
  input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${({theme}) => theme.colors.divider};
    border-radius: 50px;
    background: transparent;
    color: ${({theme}) => theme.colors.text};
    font-size: ${v.fontSizeMain};
    
    &:focus {
      outline: none;
      border-color: ${({theme}) => theme.colors.primary};
    }
  }
  
  button {
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.error};
    cursor: pointer;
    padding: 4px;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

export const AddOptionButton = styled(UnstyledButton)`
  border: 1px dashed ${({theme}) => theme.colors.divider};
  color: ${({theme}) => theme.colors.primary};
  padding: 8px 16px;
  border-radius: 8px;
  width: 100%;
  margin-top: 8px;
  
  &:hover {
    border-color: ${({theme}) => theme.colors.primary};
    background: ${({theme}) => theme.colors.shadow};
  }
`;

export const ComposeActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({theme}) => theme.colors.divider};
`;

export const MediaButtons = styled.div`
  display: flex;
  gap: 12px;
`;

export const UploadGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 0, 0, 0.08);
  border: 1.5px solid rgba(255, 0, 0, 0.22);
  border-radius: 50px;
  padding: 0 4px;
`;

export const MediaButton = styled(UnstyledButton)`
  color: ${({theme}) => theme.colors.primary};
  padding: 8px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: ${({theme}) => theme.colors.shadow};
    color: ${({theme}) => theme.colors.primary};
  }

  &.active {
    color: ${({theme}) => theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ComposeButton = styled(PrimaryButton)`
  font-weight: 600;
`;

export const DragIndicator = styled.div`
  height: 4px;
  border-radius: 2px;
  margin: 8px auto;
  cursor: grab;
  text-wrap: nowrap;
  text-align: center;
  padding-bottom: ${v.mdSpacing};
  font-style: italic;
  color: ${({theme}) => theme.colors.textSecondary};
  @media (max-width: 768px) {
    display: none;}
`;

export const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({theme}) => theme.colors.bg3};
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  margin-right: ${v.smSpacing};
`;

export const PostOptions = styled.div`
  display: flex;
  gap: ${v.smSpacing};
  align-items: center;
  padding: ${v.smSpacing};
  border-top: 1px solid ${({theme}) => theme.colors.divider};
`;

export const PostTypeSelect = styled.select`
  padding: 8px 12px;
  border-radius: ${v.borderRadius};
  border: 1px solid ${({theme}) => theme.colors.divider};
  background-color: ${({theme}) => theme.colors.bg2};
  color: ${({theme}) => theme.colors.text};
  font-size: ${v.fontSizeMain};
  outline: none;
  cursor: pointer;

  &:hover {
    border-color: ${({theme}) => theme.colors.primary};
  }
`;

export const PriceInput = styled.input.attrs({ type: 'number' })`
  padding: 8px 12px;
  border-radius: ${v.borderRadius};
  border: 1px solid ${({theme}) => theme.colors.divider};
  background-color: ${({theme}) => theme.colors.bg2};
  color: ${({theme}) => theme.colors.text};
  font-size: ${v.fontSizeMain};
  outline: none;
  width: 100px;

  /* Mobile Safari zoom prevention */
  @media (max-width: 768px) {
    font-size: 16px; /* Minimum 16px to prevent iOS Safari zoom */
    min-height: 44px; /* Apple's recommended minimum touch target */
  }

  &:focus {
    border-color: ${({theme}) => theme.colors.primary};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

export const PreviewCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${v.smSpacing};
  cursor: pointer;
  user-select: none;
  font-size: ${v.fontSizeMain};
  color: ${({theme}) => theme.colors.text};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  &:hover {
    color: ${({theme}) => theme.colors.primary};
  }
`;